define('DateCalculations.BusinessDays', [
  'underscore',
  'Moment',
  'LocationShippingTimes.Configuration'
], function DateCalculationsBusinessDays(_, moment, Configuration) {
  'use strict';

  return {
    holidayMoments: null,

    /**
     * @param {Moment} initialMoment
     * @returns {{ moment: Moment, days: number }}
     */
    getNextBusinessDay: function getNextBusinessDay(initialMoment) {
      return this.addBusinessDaysToMoment(initialMoment, 0);
    },

    /**
     * @param {Moment} initialMoment
     * @param {number} businessDays
     * @returns {{ moment: Moment, days: number }}
     */
    addBusinessDaysToMoment: function addBusinessDaysToMoment(initialMoment, businessDays) {
      var currentMoment = initialMoment.clone();
      var daysToAdd = businessDays;
      var count;
      daysToAdd += this.getNonBusinessDaysToAdd(currentMoment);
      for (count = 0; count < daysToAdd; count++) {
        currentMoment = currentMoment.add(1, 'days');
        daysToAdd += this.getNonBusinessDaysToAdd(currentMoment);
      }
      return {
        moment: initialMoment.clone().add(daysToAdd, 'days'),
        days: daysToAdd
      };
    },

    getNonBusinessDaysToAdd: function getNonBusinessDaysToAdd(dayMoment) {
      var nonBusinessDaysToAdd = this.getWeekendDaysToAdd(dayMoment);
      nonBusinessDaysToAdd += this.getHolidayDaysToAdd(dayMoment);
      return nonBusinessDaysToAdd;
    },

    /**
     * @param {Moment} dayMoment
     * @returns {number}
     */
    getWeekendDaysToAdd: function getWeekendDaysToAdd(dayMoment) {
      var daysToAdd = 0;
      if (Configuration.isExcludeSaturdays() && this.isMomentSaturday(dayMoment)) {
        daysToAdd += 1;
      }
      if (Configuration.isExcludeSundays() && this.isMomentSunday(dayMoment)) {
        daysToAdd += 1;
      }
      return daysToAdd;
    },

    /**
     * @param {Moment} dayMoment
     * @returns {boolean}
     */
    isMomentSaturday: function isMomentSaturday(dayMoment) {
      return dayMoment.day() === 6;
    },

    /**
     * @param {Moment} dayMoment
     * @returns {boolean}
     */
    isMomentSunday: function isMomentSunday(dayMoment) {
      return dayMoment.day() === 0;
    },

    /**
     * @param {Moment} dayMoment
     * @returns {number}
     */
    getHolidayDaysToAdd: function getHolidayDaysToAdd(dayMoment) {
      var m = dayMoment && typeof dayMoment.isSame === 'function' ? dayMoment.clone().startOf('day') : null;
      if (!m) {
        return 0;
      }

      var holidays = this.getHolidayMoments();
      if (!holidays || !holidays.length) {
        return 0;
      }

      var isHoliday = _.some(holidays, function (h) {
        return m.isSame(h, 'day');
      });
      return isHoliday ? 1 : 0;
    },

    /**
     * @returns {Moment[]}
     */
    getHolidayMoments: function getHolidayMoments() {
      if (!this.holidayMoments) {
        this.holidayMoments = this.calculateHolidayMoments();
      }
      return this.holidayMoments;
    },

    /**
     * @returns {Moment[]}
     */
    calculateHolidayMoments: function calculateHolidayMoments() {
      /** @type {Moment} */
      var today = moment().startOf('day');
      /** @type number */
      var currentYear = today.year();
      var nextYear = currentYear + 1;
      var holidaysMoments = [
        // January 1st
        this.getNextHolidayMoment({
          today: today,
          holidayName: "New Year's Day",
          adjust: true,
          fallback: {
            rangeStart: [currentYear, 11, 28],
            rangeEnd: [nextYear, 0, 4]
          }
        }),
        // last Monday of May
        this.getNextHolidayMoment({
          today: today,
          holidayName: 'Memorial Day',
          adjust: false,
          fallback: {
            rangeStart: [nextYear, 4, 1],
            rangeEnd: [nextYear, 4, 31]
          }
        }),
        // July 4th
        this.getNextHolidayMoment({
          today: today,
          holidayName: 'Independence Day',
          adjust: true,
          fallback: {
            rangeStart: [nextYear, 6, 1],
            rangeEnd: [nextYear, 6, 10]
          }
        }),
        // first Monday of September
        this.getNextHolidayMoment({
          today: today,
          holidayName: 'Labor Day',
          adjust: false,
          fallback: {
            rangeStart: [nextYear, 8, 1],
            rangeEnd: [nextYear, 8, 10]
          }
        }),
        // last Thursday of November
        this.getNextHolidayMoment({
          today: today,
          holidayName: 'Thanksgiving Day',
          adjust: false,
          fallback: {
            rangeStart: [nextYear, 10, 15],
            rangeEnd: [nextYear, 11, 5]
          }
        }),
        // day after last Thursday of November
        this.getNextHolidayMoment({
          today: today,
          holidayName: 'Day after Thanksgiving',
          adjust: false,
          fallback: {
            rangeStart: [nextYear, 10, 15],
            rangeEnd: [nextYear, 11, 5]
          }
        }),
        // December 25th
        this.getNextHolidayMoment({
          today: today,
          holidayName: 'Christmas Day',
          adjust: true,
          fallback: {
            rangeStart: [nextYear, 11, 20],
            rangeEnd: [nextYear, 11, 30]
          }
        })
      ];
      return _(holidaysMoments).filter(function filterHolidays(holiday) {
        return Boolean(holiday);
      });
    },

    /**
     * @param {object} options
     * @param {Moment} options.today
     * @param {string} options.holidayName
     * @param {boolean} options.adjust
     * @param {object} options.fallback
     * @param {[number, number, number]} options.fallback.rangeStart
     * @param {[number, number, number]} options.fallback.rangeEnd
     * @returns {Moment|null}
     */
    getNextHolidayMoment: function getNextHolidayMoment(options) {
      var today = options.today;
      var holidayName = options.holidayName;
      var adjust = Boolean(options.adjust);
      var rangeStart = options.fallback.rangeStart;
      var rangeEnd = options.fallback.rangeEnd;

      var holidayCurrentYear = moment().holiday(holidayName, adjust);

      var holidaysInRange = moment(rangeStart).holidaysBetween(moment(rangeEnd), adjust);
      var holidayNextYear = _(holidaysInRange || []).filter(function filterHoliday(holidayMoment) {
        return holidayMoment.isHoliday(null, adjust) === holidayName;
      })[0];

      if (!holidayCurrentYear) {
        return null;
      }
      if (holidayCurrentYear.isBefore(today)) {
        return holidayNextYear || null;
      }
      return holidayCurrentYear;
    }
  };
});
